/**
 * SBOPrint SDK JavaScript — v0.2.0
 *
 * Uso básico:
 *   const sbo = new SBOPrint({ appName: 'Mi ERP' });
 *   if (await sbo.isInstalled()) {
 *     await sbo.ensurePaired();          // 1ª vez: el usuario aprueba en el teléfono
 *     await sbo.print({ printer: 'caja', job: [ ... ] });
 *     await sbo.printPDF(blob, { printer: 'caja', idempotencyKey: 'venta-1' });
 *   }
 */
export class SBOPrintError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'SBOPrintError';
    this.status = status;
  }
}

export default class SBOPrint {
  /**
   * @param {object} [options]
   * @param {string} [options.appName]  Nombre mostrado al usuario en el pairing.
   * @param {string} [options.baseUrl]  Por defecto http://127.0.0.1:9080
   * @param {string} [options.storageKey] Clave de localStorage para la API key.
   */
  constructor(options = {}) {
    this.appName = options.appName || document.title || 'Aplicación web';
    this.baseUrl = (options.baseUrl || 'http://127.0.0.1:9080').replace(/\/+$/, '');
    this.storageKey = options.storageKey || 'sboprint.apiKey';
  }

  get apiKey() {
    try { return localStorage.getItem(this.storageKey) || null; } catch (e) { return null; }
  }

  set apiKey(value) {
    try {
      if (value) localStorage.setItem(this.storageKey, value);
      else localStorage.removeItem(this.storageKey);
    } catch (e) { /* almacenamiento no disponible */ }
  }

  /** ¿SBOPrint está instalado y corriendo? (ping con timeout corto) */
  async isInstalled(timeoutMs = 1500) {
    try {
      const res = await this._fetch('/v1/status', { method: 'GET' }, timeoutMs, false);
      const json = await res.json();
      return json && json.name === 'SBOPrint';
    } catch (e) {
      return false;
    }
  }

  /**
   * Garantiza una API key válida. Si no hay, inicia el pairing y sondea
   * hasta que el usuario apruebe en el teléfono (o expire el timeout).
   */
  async ensurePaired({ pollMs = 2000, timeoutMs = 120000 } = {}) {
    if (this.apiKey) return this.apiKey;

    const start = await this._json('/v1/pair', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: this.appName }),
    }, false);

    if (start.status === 'approved') {
      this.apiKey = start.apiKey;
      return start.apiKey;
    }
    if (start.status === 'denied') {
      throw new SBOPrintError('El usuario rechazó el acceso de esta aplicación', 403);
    }

    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, pollMs));
      const st = await this._json(`/v1/pair/${start.pairingId}`, { method: 'GET' }, false);
      if (st.status === 'approved') {
        this.apiKey = st.apiKey;
        return st.apiKey;
      }
      if (st.status === 'denied') {
        throw new SBOPrintError('El usuario rechazó el acceso de esta aplicación', 403);
      }
    }
    throw new SBOPrintError('Pairing no aprobado a tiempo: abre SBOPrint → pestaña API y aprueba la solicitud', 408);
  }

  /** Imprime un ticket con el DSL JSON. @returns {Promise<{id, status}>} */
  async print(ticket) {
    return this._json('/v1/print', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ticket),
    });
  }

  /** Imprime un PDF (Blob/ArrayBuffer) rasterizado por SBOPrint. */
  async printPDF(blob, { printer, idempotencyKey } = {}) {
    return this._binary(blob, 'application/pdf', { printer, idempotencyKey });
  }

  /** Imprime una imagen PNG/JPEG (Blob). */
  async printImage(blob, { printer, idempotencyKey } = {}) {
    const type = (blob && blob.type) || 'image/png';
    return this._binary(blob, type, { printer, idempotencyKey });
  }

  async getPrinters() {
    const json = await this._json('/v1/printers', { method: 'GET' });
    return json.printers;
  }

  async getJob(id) {
    return this._json(`/v1/jobs/${id}`, { method: 'GET' });
  }

  // ------------------------------------------------------------ internos

  async _binary(blob, contentType, { printer, idempotencyKey } = {}) {
    const params = new URLSearchParams();
    if (printer) params.set('printer', printer);
    if (idempotencyKey) params.set('idempotencyKey', idempotencyKey);
    const qs = params.toString() ? `?${params}` : '';
    return this._json(`/v1/print${qs}`, {
      method: 'POST',
      headers: { 'Content-Type': contentType },
      body: blob,
    });
  }

  async _json(path, init, auth = true, allowReauth = true) {
    const res = await this._fetch(path, init, 15000, auth);
    let json = null;
    try { json = await res.json(); } catch (e) { /* sin cuerpo */ }
    if (!res.ok) {
      if (auth && allowReauth && (res.status === 401 || res.status === 403)) {
        this.apiKey = null; // key inválida o revocada: re-parear y reintentar una vez
        await this.ensurePaired();
        return this._json(path, init, auth, false);
      }
      throw new SBOPrintError((json && json.error) || `HTTP ${res.status}`, res.status);
    }
    return json;
  }

  async _fetch(path, init = {}, timeoutMs = 15000, auth = true) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const headers = Object.assign({}, init.headers);
      if (auth && this.apiKey) headers['Authorization'] = `Bearer ${this.apiKey}`;
      return await fetch(this.baseUrl + path, Object.assign({}, init, {
        headers,
        signal: controller.signal,
        mode: 'cors',
      }));
    } finally {
      clearTimeout(timer);
    }
  }
}
